import React from 'react'
import { leagueIds, ratePlanCodes } from 'src/constants'
import { networkLogos } from 'src/constants/networkLogos'
import { isUSFreeTrial } from 'src/modules/utils/constants'

export const slingData = {
  competitor: 'Sling TV',
  competitorColor: '#00B9FF',
  heroData: {
    title: 'Looking for the best streaming service?',
    subtitle:
      'Compare Fubo & Sling TV on price, features, channels and add-ons to see which is better for you.',
  },
  generalCompareData: {
    heading: 'Compare Fubo Sports vs. Sling TV',
    subheading:
      "For sports fans looking for a more affordable option with key sports channels, here's how Fubo Sports compares to Sling TV.",
    listItems: [
      'Channel Count',
      'Starting Price',
      'ESPN+ and ESPN Unlimited',
      'ESPN',
      'Fox, ABC and CBS',
      // TEMPORARY NBC DISPUTE
      // 'NBC',
      'Free Trial',
    ],
    fuboList: [
      '20+',
      <>
        $55.99/mo*
        <div className="note">$10 off your first month</div>
      </>,
      'Yes',
      'Yes',
      // TEMPORARY NBC DISPUTE
      // 'Yes',
      'No',
      isUSFreeTrial ? 'Yes' : 'No',
    ],
    competitorList: [
      '46+',
      <>
        $45.99-$65.99/mo*
        <div className="note">No free trial</div>
      </>,
      'No',
      'Yes',
      'Select markets only',
      'Select markets only',
      'No',
    ],
    fuboLogoText: 'Sports plan',
    offerCode: ratePlanCodes.fubo_sports_monthly,
  },
  generalCompareDataAlt: {
    heading: 'Compare Fubo vs. Sling TV',
    subheading:
      "Here's a general look at both to help you determine which is the best streaming service for you.",
    listItems: [
      'Channel Count',
      'Starting Price',
      'ESPN+ and ESPN Unlimited',
      'ESPN',
      'Fox, ABC, CBS',
      // 'Fox, ABC, NBC and CBS',
      // TEMPORARY NBC DISPUTE
      'Local RSNs',
      'Free Trial',
    ],
    fuboList: [
      '185+',
      <>
        $73.99/mo*
        <div className="note">$25 off your first month</div>
      </>,
      'Yes',
      'Yes',
      'Yes',
      'Yes',
      'Yes',
      isUSFreeTrial ? 'Yes' : 'No',
    ],
    competitorList: [
      '30-50+',
      <>
        $45.99-$65.99/mo*
        <div className="note">No free trial</div>
      </>,
      'No',
      'Yes',
      'Select markets only',
      'No',
      'No',
    ],
    fuboLogoText: 'Pro plan',
    offerCode: ratePlanCodes.pro,
  },
  prosConsData: {
    heading: 'Pros and Cons: Fubo vs. Sling TV',
    content: (
      <>
        <p>
          Fubo and Sling TV have a lot of the features cord cutters want from a cable replacement
          streaming service. They check a lot of the boxes like maintaining a relatively low price,
          offering a wide variety of live channels and including unique features cable companies
          just don’t have.
        </p>
        <p>
          Despite both offering a bunch of plans and add-on options to create a customized TV
          experience, they still have some extensive differences between them. Fubo is a more
          premium product and it has the price tag to match. Fubo plans are loaded with live
          channels (at least 185 live channels) and quality features. Eligible plans also include
          ESPN Unlimited, giving users access to additional live events and original content through
          the ESPN app. Every plan currently includes
          {isUSFreeTrial ? ' a free trial,' : ''} unlimited Cloud DVR storage and the ability to
          simultaneously stream on multiple devices (up to 10).
        </p>
        <p>
          Sling TV is definitely a cheaper product, but it’s at a significant cost to the channel
          count. Both the Orange and Blue plans from Sling TV lack many of the sports and
          entertainment channels under the CBS umbrella, which includes channels needed for football
          on Saturdays and Sundays. Sling TV has good features, but they sometimes have complicated
          restrictions such as only getting a free trial if you signed up on a specific platform or
          limiting the ability to simultaneously stream to select channels.
        </p>
      </>
    ),
  },
  plansData: {
    heading: 'Compare Fubo Pro plan vs. Sling TV Orange & Blue plan',
    subheading:
      "Let's take a look at the differences between the popular plans for Fubo and Sling TV.",
    tableData: {
      Plan: ['Pro plan', 'Orange & Blue Plan'],
      Channels: ['185+ channels', '48+ channels'],
      'Monthly Price': ['$73.99', '$65.99'],
      'Hours of DVR storage': ['Unlimited', '50 hours'],
      'Free Trial': [isUSFreeTrial ? 'Yes' : 'No', 'No'],
      'Multiple active streams': ['Up to 10', 'Up to 4, with restrictions'],
      'Regional sports networks': [true, true],
      'Streaming from any device': [true, true],
      'ESPN Unlimited included': [true, false],
    },
  },
  plansDataAlt: {
    heading: 'Compare Fubo Sports plan vs. Sling TV Orange & Blue plan',
    subheading:
      "For sports fans looking for more value, here's how Fubo Sports compares to Sling TV Orange & Blue.",
    tableData: {
      Plan: ['Fubo Sports', 'Orange & Blue Plan'],
      Channels: ['20+ sports channels', '46+ channels'],
      'Monthly Price': ['$55.99', '$65.99'],
      'Hours of DVR storage': ['Unlimited', '50 hours'],
      'Free Trial': [isUSFreeTrial ? 'Yes' : 'No', 'No'],
      'Multiple active streams': ['Up to 10', 'Up to 4, with restrictions'],
      'Streaming from any device': [true, true],
      'ESPN Unlimited included': [true, false],
    },
  },
  channelsData: {
    heading: 'Compare Fubo Channels vs. Sling TV Channels',
    subheading:
      'This channel comparison chart focuses on what’s available among the most-watched channels from 2025 in the Fubo Pro plan vs. Sling TV Orange & Blue plan.',
    networksData: [
      { network: networkLogos.abc, fubo: true, competitor: true },
      { network: networkLogos.cbs, fubo: true, competitor: false },
      // TEMPORARY NBC DISPUTE
      // { network: networkLogos.nbc, fubo: true, competitor: true },
      { network: networkLogos.fox, fubo: true, competitor: true },
      { network: networkLogos.espn, fubo: true, competitor: true },
      { network: networkLogos.mtv, fubo: true, competitor: false },
      // TEMPORARY NBC DISPUTE
      // { network: networkLogos.telemundo, fubo: true, competitor: false },
      { network: networkLogos.disneyjunior, fubo: true, competitor: false },
      { network: networkLogos.fs1, fubo: true, competitor: true },
      { network: networkLogos.ion, fubo: true, competitor: false },
      //REMOVED: univision_removed dec24
      // { network: networkLogos.univision, fubo: true, competitor: false },
      { network: networkLogos.hallmark, fubo: true, competitor: false },
      { network: networkLogos.nickelodeon, fubo: true, competitor: false },
      // TEMPORARY NBC DISPUTE
      // { network: networkLogos.usa, fubo: true, competitor: true },
      // { network: networkLogos.oxygen, fubo: true, competitor: false },
    ],
  },
  completedChannelsData: {
    heading: 'Complete Channel Comparison',
    subheading:
      'See how the live channel lineups compare between Fubo Pro and Sling TV Orange & Blue.',
    content: (
      <>
        <p>
          Both the Fubo Pro plan and Sling TV Orange & Blue plan have an excellent channel lineup.
          Cord cutters can definitely find a nice mix of live sports, top TV shows, popular movies
          and breaking news with either direction. The big difference comes down to what fits your
          needs and the needs of your household.
        </p>
        {/*REMOVED: univision_removed*/}
        {/* <p>
          The live channel lineup for Fubo is a more robust offering, and it's not even close. With
          185+ channels at $73.99, they're actually the better value per channel overall. Fubo's the
          superior streaming service for sports, which includes significantly better coverage during
          football season due to their CBS channels. Fubo is more than sports though. They have many
          of the most watched live TV channels. They have popular news networks like FOX News
          Channel and MS NOW as well as popular entertainment channels like USA Network, Hallmark
          Channel and ION. They even have Spanish-language channels like Telemundo and Univision.
        </p> */}
        {/* REPLACED: univision_removed */}
        {/* TEMPORARY NBC DISPUTE - Original text before NBC removal:
          Channel and MS NOW as well as popular entertainment channels like USA Network, Hallmark
          Channel and ION. They even have Spanish-language channels like Telemundo.

          They do overlap with Fubo on a handful of the same top channels like NBC, ABC, FOX, ESPN,
          USA Network and others. As noted earlier, they are missing a significant portion of
        */}
        <p>
          The live channel lineup for Fubo is a more robust offering, and it's not even close. With
          185+ channels at $73.99, they're actually the better value per channel overall. Fubo's the
          superior streaming service for sports, which includes significantly better coverage during
          football season due to their CBS channels. Fubo is more than sports though. They have many
          of the most watched live TV channels. They have popular news networks like FOX News
          Channel as well as popular entertainment channels like Hallmark Channel and ION.
        </p>
        <p>
          However, Sling TV, as we already covered, is the cheaper product, but not by that much.
          They do overlap with Fubo on a handful of the same top channels like ABC, FOX, ESPN and
          others. As noted earlier, they are missing a significant portion of popular TV with the
          absence of the Paramount Media Networks, which includes CBS, MTV, Nickelodeon, CMT and
          more. However, they do have the WarnerMedia channels that Fubo doesn't currently include
          in their lineup. Overall, Sling TV has a decent lineup of sports and entertainment, but it
          does require you having the Orange & Blue plan to enjoy it all.
        </p>
        <p>
          If you're primarily interested in sports, it's worth noting that Fubo Sports offers a more
          affordable option with a strong focus on ESPN and key sports channels at $55.99/mo, giving
          you more simultaneous streams and unlimited DVR compared to Sling TV's Orange & Blue plan.
        </p>
      </>
    ),
  },
  faqData: {
    qaData: [
      {
        id: 'fubo-faq-sports-plan',
        question: 'Does Fubo have a more affordable plan than Sling TV?',
        answer: (
          <p>
            Yes, Fubo Sports is a sports-focused plan at $55.99/mo, which is $10 less than Sling TV
            Orange & Blue. While it has fewer total channels (20+ sports-focused channels), it gives
            you more simultaneous streams (up to 10 vs. 4) and unlimited DVR storage for sports fans
            looking to save money.
          </p>
        ),
      },
      {
        id: 'fubo-faq-5',
        question: 'What channels does Fubo have over Sling TV?',
        answer: (
          <p>
            Fubo has the Paramount Media Networks which includes CBS, MTV, Nickelodeon, VH1, CMT and
            others. They also have channels like ION, Big Ten Network and Hallmark Channel.
          </p>
        ),
      },
    ],
  },
  // Show multiple sports (NFL, NBA, NHL, College Basketball, College Football)
  matchtickerSportId: null, // Not needed when using multiple leagues
  matchtickerLeagueName: 'Multiple Sports',
  matchtickerLeagueId: [
    leagueIds.NFL,
    leagueIds.NBA,
    leagueIds.NHL,
    leagueIds["Men's NCAA Basketball"],
    leagueIds['NCAA Football'],
  ],
}
